import React, { useState } from "react";
import {
  Container, Typography, Box, Paper, Tabs, Tab, TextField,
  InputAdornment, IconButton,
} from "@mui/material";
import { Search, ArrowBackIos } from "@mui/icons-material";
import { useLocation, Link, useNavigate } from "react-router-dom";
import Dashboard from "./Dashboard";
import Productos from "./Productos";
import CuentaCorriente from "./CuentaCorriente";
import NuevoPresupuesto from "./NuevoPresupuesto";

// Componente principal de gestión de paquetes
export const PackagesPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Estados principales
  const [tabValue, setTabValue] = useState<number>(() => {
    const state = location.state as { tab?: number };
    return state?.tab !== undefined ? state.tab : 0;
  });
  const [terminoBusqueda, setTerminoBusqueda] = useState<string>('');
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Efecto para actualizar la pestaña si viene por estado de navegación
  React.useEffect(() => {
    const state = location.state as { tab?: number };
    if (state?.tab !== undefined) {
      setTabValue(state.tab);
    }
  }, [location.state]);

  // Configuración de pestañas disponibles
  const pestañas = [
    { label: "Paquetes", component: Productos, disabled: false },
    { label: "Nuevo Presupuesto", component: NuevoPresupuesto, disabled: false },
    { label: "Dashboard", component: Dashboard, disabled: false },
    { label: "Cuenta Corriente", component: CuentaCorriente, disabled: false },
  ];

  // Handlers
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => setTabValue(newValue);
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => setTerminoBusqueda(event.target.value);

  // Callback cuando se crea un paquete exitosamente
  const handlePackageCreated = () => {
    setTabValue(0); // Cambiar a pestaña Paquetes
    setRefreshTrigger(prev => prev + 1); // Forzar recarga del listado
  };

  // Callback cuando se cierra la ficha de un paquete (para volver si es necesario)
  const handleCloseFicha = () => {
    const state = location.state as { returnTab?: number };
    if (state?.returnTab !== undefined) {
      const targetTab = state.returnTab;
      // Limpiar el estado de navegación para no quedar en un bucle
      navigate(location.pathname, { replace: true, state: {} });
      setTabValue(targetTab);
    }
  };

  // Renderizar contenido según pestaña
  const renderizarContenido = () => {
    // Pasar terminoBusqueda y refreshTrigger a Productos (pestaña 0)
    if (tabValue === 0) {
      const state = location.state as { openPackageId?: number };
      return (
        <Productos 
          terminoBusqueda={terminoBusqueda} 
          refreshTrigger={refreshTrigger} 
          openPackageId={state?.openPackageId} 
          onCloseFicha={handleCloseFicha}
        />
      );
    }
    // Pasar callback a NuevoPresupuesto (pestaña 1)
    if (tabValue === 1) {
      return <NuevoPresupuesto onPackageCreated={handlePackageCreated} />;
    }
    const ComponenteSeleccionado = pestañas[tabValue].component;
    return <ComponenteSeleccionado />;
  };

  return (
    <Container maxWidth="xl">
      {/* Header con navegación */}
      <Typography variant="h5" sx={{ mb: 3, mt: 4.5 }}>
        <IconButton aria-label="volver" size="small" component={Link} to="/">
          <ArrowBackIos fontSize="small" />
        </IconButton>
        Paquetes Individuales
      </Typography>

      {/* Panel de navegación con pestañas y buscador */}
      <Paper elevation={2} sx={{ borderRadius: 3, border: 1, borderColor: 'divider', px: 2, py: 1, mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          {/* Menú de pestañas */}
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="navegación paquetes"
            sx={{ minHeight: 48 }}
          >
            {pestañas.map((pestaña, index) => (
              <Tab 
                key={index} 
                label={pestaña.label} 
                disabled={pestaña.disabled} 
                sx={{ 
                  minHeight: 48, 
                  mr: 4,
                  '&.Mui-disabled': {
                    color: 'rgba(0, 0, 0, 0.15)',
                  }
                }} 
              />
            ))}
          </Tabs>

          {/* Buscador de paquetes */}
          <Box sx={{ my: 1, minWidth: 300 }}>
            <TextField
              size="small"
              placeholder="Buscar paquetes, clientes..."
              value={terminoBusqueda}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '100%' }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Contenido dinámico según pestaña seleccionada */}
      {renderizarContenido()}
    </Container>
  );
};

export default PackagesPage;
